import { Alert } from '@analyzer/client';
import {
  Alert as MuiAlert,
  AlertTitle,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import React from 'react';
import { getSeverityColor } from './severity.js';

interface WalletProviderCall {
  type: 'Ethereum';
  method: string;
  details: Record<string, any>;
  stack?: string;
  cdpUrl?: string | null;
  cdpFunctionName?: string | null;
  cdpLineNumber?: number | null;
  cdpColumnNumber?: number | null;
}

interface WalletProviderAlertProps {
  alert: Alert & {
    data: {
      page: string;
      calls: WalletProviderCall[];
      newMethods?: string[];
      astEquivalenceMap?: Record<string, string>;
      requestMatcherCandidates?: number;
      normalizedSignatures?: Array<{
        call: string;
        signature: string;
        isNew: boolean;
        knownFile?: boolean;
        sha?: string;
        previousSha?: string;
        astEquivalent?: string;
      }>;
    };
  };
}

const groupCallsByType = (calls: WalletProviderCall[]) => {
  const grouped: Record<string, WalletProviderCall[]> = {};
  calls.forEach((call) => {
    if (!grouped[call.type]) {
      grouped[call.type] = [];
    }
    grouped[call.type]!.push(call);
  });
  return grouped;
};

const getLocationString = (call: WalletProviderCall): string | null => {
  if (call.cdpUrl) {
    const line = call.cdpLineNumber ?? '?';
    const col = call.cdpColumnNumber ?? '?';
    return `${call.cdpUrl}:${line}:${col}`;
  }
  if (call.stack) {
    const match = call.stack.match(/https?:\/\/[^\s)]+/);
    if (match) return match[0];
  }
  return null;
};

export function WalletProviderAlert({ alert }: WalletProviderAlertProps) {
  if (!alert || !alert.data) {
    return null;
  }

  const groupedCalls = groupCallsByType(alert.data.calls);
  const totalAttempts = alert.data.calls.length;
  const newMethods = alert.data.newMethods || [];
  const newMethodsSet = new Set(newMethods);
  const astEquivalenceMap = alert.data.astEquivalenceMap || {};
  const requestMatcherCandidates = alert.data.requestMatcherCandidates ?? 0;
  const normalizedSignatures = alert.data.normalizedSignatures || [];

  const summaryRows = (() => {
    const map = alert.data.calls.reduce(
      (acc, call) => {
        const methodLabel =
          call.method === 'dispatchEvent' && call.details?.eventType
            ? `${call.method} (${call.details.eventType})`
            : call.method;
        const key = `${call.type}|${call.method}`;
        const existing = acc.get(key) || {
          type: call.type,
          method: call.method,
          methodLabel,
          count: 0,
          locations: [] as Array<{ loc: string; fn: string | null }>
        };
        existing.count += 1;
        const loc = getLocationString(call);
        if (loc) {
          const locKey = `${loc}|${call.cdpFunctionName || ''}`;
          if (!existing.locations.some((l) => `${l.loc}|${l.fn || ''}` === locKey)) {
            existing.locations.push({ loc, fn: call.cdpFunctionName || null });
          }
        }
        acc.set(key, existing);
        return acc;
      },
      new Map<
        string,
        {
          type: WalletProviderCall['type'];
          method: string;
          methodLabel: string;
          count: number;
          locations: Array<{ loc: string; fn: string | null }>;
        }
      >()
    );

    return Array.from(map.values());
  })();

  return (
    <Box>
      <MuiAlert severity={getSeverityColor(alert.severity)} sx={{ mb: 2 }}>
        <AlertTitle sx={{ fontSize: '1.1rem' }}>
          Monitored API Calls Detected
          <Chip
            label={alert.severity.toUpperCase()}
            color={getSeverityColor(alert.severity)}
            size="small"
            sx={{ ml: 2 }}
          />
        </AlertTitle>
        <Typography variant="body2">
          {totalAttempts} monitored API call{totalAttempts !== 1 ? 's' : ''} detected across{' '}
          {Object.keys(groupedCalls).length} categor{Object.keys(groupedCalls).length !== 1 ? 'ies' : 'y'}.
        </Typography>
      </MuiAlert>

      {newMethods.length > 0 && (
        <MuiAlert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>New Monitored API Methods Detected</AlertTitle>
          <Typography variant="body2">
            {newMethods.length} new method{newMethods.length !== 1 ? 's' : ''} not present in the previous
            scan:
          </Typography>
          <Box sx={{ mt: 1 }}>
            {newMethods.map((method) => (
              <Chip key={method} label={method} size="small" color="warning" sx={{ mr: 1, mt: 0.5 }} />
            ))}
          </Box>
        </MuiAlert>
      )}

      <Paper elevation={2}>
        <Card>
          <CardContent>
            <Box sx={{ '& > :not(:last-child)': { mb: 3 } }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Description
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Monitors fingerprint-relevant browser APIs (window.ethereum, EIP-6963 dispatch).
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" gutterBottom>
                  Monitored API Summary
                </Typography>
                {summaryRows.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Type</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Method</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Sources</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>Count</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {summaryRows
                          .slice()
                          .sort((a, b) => {
                            const aZero = a.count === 0;
                            const bZero = b.count === 0;
                            if (aZero !== bZero) return aZero ? 1 : -1;
                            return (
                              a.type.localeCompare(b.type) ||
                              (a.methodLabel || a.method).localeCompare(b.methodLabel || b.method)
                            );
                          })
                          .map(({ type, method, methodLabel, count, locations }, idx) => {
                            const isNew = newMethodsSet.has(`${type}.${method}`);
                            return (
                              <TableRow key={idx}>
                                <TableCell>
                                  <Chip label={type} size="small" color="primary" />
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                  {methodLabel}
                                  {isNew && <Chip label="NEW" color="warning" size="small" sx={{ ml: 1 }} />}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.75rem',
                                    wordBreak: 'break-all'
                                  }}
                                >
                                  {locations.length === 0 ? (
                                    '—'
                                  ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                      {locations.map(({ loc, fn }, i) => (
                                        <Box key={i}>
                                          {fn && (
                                            <Typography
                                              component="span"
                                              sx={{ color: 'text.secondary', fontSize: '0.7rem', mr: 0.5 }}
                                            >
                                              {fn}:
                                            </Typography>
                                          )}
                                          {loc}
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                </TableCell>
                                <TableCell align="right">{count}x</TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <MuiAlert severity="info">No monitored API calls recorded.</MuiAlert>
                )}
              </Box>

              <Divider />

              {'astEquivalenceMap' in alert.data && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      AST Equivalence Map
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {requestMatcherCandidates === 0
                        ? 'No renamed JS file pairs found — either no files were renamed or the naming pattern was not recognized.'
                        : `${requestMatcherCandidates} JS file pair${requestMatcherCandidates !== 1 ? 's' : ''} analyzed · ${Object.keys(astEquivalenceMap).length} matched (identical AST) · ${requestMatcherCandidates - Object.keys(astEquivalenceMap).length} did not match (code changed)`}
                    </Typography>
                    {Object.keys(astEquivalenceMap).length > 0 ? (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <strong>Current URL</strong>
                              </TableCell>
                              <TableCell>
                                <strong>Previous URL (AST-equivalent)</strong>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {Object.entries(astEquivalenceMap).map(([current, previous]) => (
                              <TableRow key={current}>
                                <TableCell
                                  sx={{
                                    fontSize: '0.75rem',
                                    fontFamily: 'monospace',
                                    wordBreak: 'break-all'
                                  }}
                                >
                                  {current}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontSize: '0.75rem',
                                    fontFamily: 'monospace',
                                    wordBreak: 'break-all'
                                  }}
                                >
                                  {previous}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" color="text.disabled">
                        No AST-equivalent file mappings detected.
                      </Typography>
                    )}
                  </Box>

                  {normalizedSignatures.length > 0 && (
                    <>
                      <Divider />
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Normalized Signatures
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>
                                  <strong>Call</strong>
                                </TableCell>
                                <TableCell>
                                  <strong>Signature</strong>
                                </TableCell>
                                <TableCell>
                                  <strong>Current SHA</strong>
                                </TableCell>
                                <TableCell>
                                  <strong>Previous SHA</strong>
                                </TableCell>
                                <TableCell>
                                  <strong>AST Match</strong>
                                </TableCell>
                                <TableCell>
                                  <strong>Status</strong>
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(() => {
                                const seen = new Set<string>();
                                return normalizedSignatures.filter((s) => {
                                  const key = `${s.call}|${s.signature}`;
                                  if (seen.has(key)) return false;
                                  seen.add(key);
                                  return true;
                                });
                              })().map((s, idx) => {
                                const shaMatch = s.sha && s.previousSha && s.sha === s.previousSha;
                                return (
                                  <TableRow
                                    key={idx}
                                    sx={{
                                      bgcolor: s.isNew
                                        ? s.knownFile
                                          ? 'info.light'
                                          : 'warning.light'
                                        : undefined
                                    }}
                                  >
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                      {s.call}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        fontFamily: 'monospace',
                                        fontSize: '0.75rem',
                                        wordBreak: 'break-all'
                                      }}
                                    >
                                      {s.signature}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                      {s.sha ? s.sha.slice(0, 8) : '—'}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        fontFamily: 'monospace',
                                        fontSize: '0.75rem',
                                        color: shaMatch ? 'success.main' : undefined
                                      }}
                                    >
                                      {s.previousSha ? s.previousSha.slice(0, 8) : '—'}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        fontFamily: 'monospace',
                                        fontSize: '0.7rem',
                                        wordBreak: 'break-all',
                                        color: 'text.secondary'
                                      }}
                                    >
                                      {s.astEquivalent ? s.astEquivalent : '—'}
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={s.isNew ? (s.knownFile ? 'NEW · same SHA' : 'NEW') : 'SEEN'}
                                        color={s.isNew ? (s.knownFile ? 'info' : 'warning') : 'success'}
                                        size="small"
                                      />
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </>
                  )}
                </>
              )}

              <Divider />

              <Box>
                <Typography variant="h6" gutterBottom>
                  Raw Calls
                </Typography>
                {alert.data.calls.length > 0 ? (
                  <Paper
                    variant="outlined"
                    sx={{ p: 1, maxHeight: 240, overflow: 'auto', bgcolor: 'grey.50' }}
                  >
                    <pre style={{ fontSize: '0.8rem', margin: 0 }}>
                      {JSON.stringify(alert.data.calls, null, 2)}
                    </pre>
                  </Paper>
                ) : (
                  <MuiAlert severity="warning">No monitored API calls recorded.</MuiAlert>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Paper>
    </Box>
  );
}
